var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React from "react";
import { FormattedMessage, FormattedDate, useIntl } from "react-intl";
import { Pane, Button, Icon, KeyValue, Row, Col, AccordionSet, Accordion, Badge, ConfirmationModal, Loading, Callout, MultiColumnList, } from "@folio/stripes/components";
import { useOkapiKy } from "@folio/stripes/core";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { UserStatusEnum, } from "../types/api";
import { fetchOrganizations } from "../api/organizationService";
import { useModal } from "../utils/useModal";
import { getErrorInfo } from "../utils/getErrorCode";
import { getLoadFailureMessageId, getUserErrorMessageId, } from "../utils/getErrorMessageId";
import { usePermissions } from "../utils/permissionsUtils";
import { authorityTypeLabel, roleScreenAuthorityTypes, } from "../utils/authorityTypes";
import useAuthorityTypes from "../hooks/useAuthorityTypes";
import { classifyAssignment, describeAssignment, rowsOfType, } from "../utils/nkRoles";
import StatusBadge from "./StatusBadge";
import AuditLogAccordion from "./AuditLogAccordion";
import RoleValidity from "./RoleValidity";
import SetPasswordModal from "./SetPasswordModal";
import ActionErrorModal from "./ActionErrorModal";
/**
 * CZNA-1612 — the greyed-out look of an action that cannot run on the own account, applied
 * by hand. Those items are aria-disabled rather than `disabled` (see `selfDisabledItem`),
 * and every dimming rule in stripes-components keys on the `disabled` attribute — there is
 * no `[aria-disabled]` selector in the library. Same values as stripes' own `:disabled`
 * look and as ListActionsMenu (ui-czna-authorities, CZNA-537). Inline because this module
 * carries no CSS files.
 */
var SELF_DISABLED_ITEM_STYLE = {
    opacity: 0.65,
    cursor: "default",
};
/** The reason line rendered under such an item (ListActionsMenu's DISABLED_REASON_STYLE). */
var SELF_DISABLED_REASON_STYLE = {
    padding: "0 1rem 0.5rem 2.4rem",
    maxWidth: "28rem",
    fontSize: "0.85em",
    lineHeight: 1.35,
    color: "#5b5b5b",
};
var UserDetailPane = function (_a) {
    var _b, _c, _d;
    var id = _a.id, onClose = _a.onClose, onEdit = _a.onEdit;
    var intl = useIntl();
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var _e = useModal(), isErrorOpen = _e.isOpen, errorMessage = _e.message, openErrorModal = _e.openModal, closeErrorModal = _e.closeModal;
    var _f = usePermissions(), canEditUser = _f.canEditUser, canDeleteUser = _f.canDeleteUser;
    var _g = React.useState(false), showArchiveModal = _g[0], setShowArchiveModal = _g[1];
    var _h = React.useState(false), showRestoreModal = _h[0], setShowRestoreModal = _h[1];
    var _j = React.useState(false), showSetPasswordModal = _j[0], setShowSetPasswordModal = _j[1];
    var _k = React.useState(false), showResetPasswordModal = _k[0], setShowResetPasswordModal = _k[1];
    var calloutRef = React.useRef(null);
    var _l = useQuery(["czna-users", "user", id], function () {
        return ky.get("czna-users/users/".concat(id)).json();
    }), user = _l.data, isLoading = _l.isLoading, error = _l.error;
    // Fetch the current logged-in user to prevent self-archive
    var currentUser = useQuery(["czna-users", "me"], function () { return ky.get("czna-users/users/me").json(); }, { staleTime: 5 * 60 * 1000, cacheTime: 10 * 60 * 1000 }).data;
    // Fetch the user's organization for display (US-02)
    var organization = useQuery(["czna-users", "organization", user === null || user === void 0 ? void 0 : user.organizationId], function () {
        return ky
            .get("czna-users/organizations/".concat(user.organizationId))
            .json();
    }, { enabled: !!(user === null || user === void 0 ? void 0 : user.organizationId) }).data;
    /**
     * Organizations a stored authority-type row can be scoped to. CZNA-1598: only a
     * non-standard assignment names them (a standard one reads as its NK role), and such a row
     * may point at a library the user does not belong to — hence not just the user's own.
     */
    var scopeOrganizations = useQuery(["czna-organizations", "roleScopes"], function () { return fetchOrganizations(ky, { limit: 500 }); }, { enabled: ((_c = (_b = user === null || user === void 0 ? void 0 : user.authorityTypeRoles) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0 }).data;
    /**
     * CZNA-650: which rows the role table lists. Every record type, held or not — that is the
     * overview this table has always given. Vocabulary types only when held: the registry is
     * ~30 types most users never get, and listing them all would bury the grants that exist.
     * A held type the list no longer names still shows, by its code (see withAssignedTypes).
     */
    var _m = useAuthorityTypes(), authorityTypes = _m.authorityTypes, authorityTypesLoading = _m.isLoading, authorityTypesFailed = _m.isError;
    var authorityTypeRows = roleScreenAuthorityTypes(intl, authorityTypes, ((_d = user === null || user === void 0 ? void 0 : user.authorityTypeRoles) !== null && _d !== void 0 ? _d : []).map(function (r) { return r.authorityType; }), { unheldRecordTypes: true });
    var isActive = (user === null || user === void 0 ? void 0 : user.status) === UserStatusEnum.ACTIVE;
    var isSelf = (currentUser === null || currentUser === void 0 ? void 0 : currentUser.id) === id;
    var archiveMutation = useMutation(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, ky.delete("czna-users/users/".concat(id))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, {
        onSuccess: function () {
            var _a;
            queryClient.invalidateQueries(["czna-users"]);
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: "success",
                message: intl.formatMessage({ id: "ui-czna-users.users.action.archive.success" }, { name: "".concat(user === null || user === void 0 ? void 0 : user.firstName, " ").concat(user === null || user === void 0 ? void 0 : user.lastName) }),
            });
        },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var errorInfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        errorInfo = _a.sent();
                        openErrorModal(intl.formatMessage({ id: getUserErrorMessageId(errorInfo) }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var restoreMutation = useMutation(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, ky.post("czna-users/users/".concat(id, "/restore"))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, {
        onSuccess: function () {
            var _a;
            queryClient.invalidateQueries(["czna-users"]);
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: "success",
                message: intl.formatMessage({ id: "ui-czna-users.users.action.restore.success" }, { name: "".concat(user === null || user === void 0 ? void 0 : user.firstName, " ").concat(user === null || user === void 0 ? void 0 : user.lastName) }),
            });
        },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var errorInfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        errorInfo = _a.sent();
                        openErrorModal(intl.formatMessage({ id: getUserErrorMessageId(errorInfo) }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var resetPasswordMutation = useMutation(function () { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, ky.post("czna-users/users/".concat(id, "/password-reset"))];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, {
        onSuccess: function () {
            var _a;
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: "success",
                message: intl.formatMessage({
                    id: "ui-czna-users.users.action.resetPassword.success",
                }),
            });
        },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var errorInfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        errorInfo = _a.sent();
                        openErrorModal(intl.formatMessage({ id: getUserErrorMessageId(errorInfo) }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var setPasswordMutation = useMutation(function (_a) { return __awaiter(void 0, [_a], void 0, function (_b) {
        var password = _b.password, temporary = _b.temporary;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0: return [4 /*yield*/, ky.post("czna-users/users/".concat(id, "/set-password"), {
                        json: { password: password, temporary: temporary },
                    })];
                case 1:
                    _c.sent();
                    return [2 /*return*/];
            }
        });
    }); }, {
        onSuccess: function () {
            var _a;
            setShowSetPasswordModal(false);
            (_a = calloutRef.current) === null || _a === void 0 ? void 0 : _a.sendCallout({
                type: "success",
                message: intl.formatMessage({
                    id: "ui-czna-users.users.action.setPassword.success",
                }),
            });
        },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var errorInfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        errorInfo = _a.sent();
                        openErrorModal(intl.formatMessage({ id: getUserErrorMessageId(errorInfo) }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var handleEdit = function () {
        onEdit(id);
    };
    var handleArchive = function () {
        setShowArchiveModal(true);
    };
    var handleConfirmArchive = function () {
        setShowArchiveModal(false);
        archiveMutation.mutate();
    };
    var handleRestore = function () {
        setShowRestoreModal(true);
    };
    var handleConfirmRestore = function () {
        setShowRestoreModal(false);
        restoreMutation.mutate();
    };
    var handleResetPassword = function () {
        setShowResetPasswordModal(true);
    };
    var handleConfirmResetPassword = function () {
        setShowResetPasswordModal(false);
        resetPasswordMutation.mutate();
    };
    /**
     * An action-menu item that cannot run on the own account — the backend refuses to archive
     * the current user or to reset or set their password (CZNA-1612). It is shown unavailable
     * with its reason rendered underneath, following ListActionsMenu (ui-czna-authorities,
     * CZNA-537):
     *
     * - `aria-disabled`, not `disabled`. Stripes Button.css gives `[disabled]`
     *   `pointer-events: none`, and a disabled button cannot take focus, so a hint attached
     *   to a disabled item is never shown on hover and never announced on focus. An
     *   aria-disabled button stays focusable, is announced as unavailable, and is described by
     *   its reason. No click handler is attached, so nothing runs.
     * - The reason is rendered as a line under the item, not hung on a hover tooltip: it is
     *   visible as soon as the menu opens, for the mouse and the keyboard user alike.
     *   `role="none"` keeps the line from posing as a menu item; the item's
     *   aria-describedby is what delivers it to a screen reader.
     *
     * `itemKey` makes the reason's id unique: two items may carry the same message, and a
     * shared id would point every aria-describedby at whichever line rendered first.
     */
    var selfDisabledItem = function (itemKey, label, reasonMessageId) {
        var reasonId = "user-detail-".concat(itemKey, "-self-disabled-reason");
        return (React.createElement(React.Fragment, null,
            React.createElement(Button, { buttonStyle: "dropdownItem", "aria-disabled": "true", "aria-describedby": reasonId, style: SELF_DISABLED_ITEM_STYLE }, label),
            React.createElement("div", { id: reasonId, role: "none", style: SELF_DISABLED_REASON_STYLE },
                React.createElement(FormattedMessage, { id: reasonMessageId }))));
    };
    /** A password action: unavailable on the own account (CZNA-1612), as today otherwise. */
    var passwordAction = function (itemKey, labelId, onClick) {
        var label = (
        // @ts-expect-error - stripes Icon supports "key" but type definition omits it
        React.createElement(Icon, { icon: "key" },
            React.createElement(FormattedMessage, { id: labelId })));
        return isSelf ? (selfDisabledItem(itemKey, label, "ui-czna-users.users.action.password.selfDisabled")) : (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: onClick }, label));
    };
    var actionMenu = function (_a) {
        var onToggle = _a.onToggle;
        return (React.createElement(React.Fragment, null,
            canEditUser && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                    onToggle();
                    handleEdit();
                } },
                React.createElement(Icon, { icon: "edit" },
                    React.createElement(FormattedMessage, { id: "ui-czna-users.edit" })))),
            canDeleteUser &&
                isActive &&
                (isSelf ? (selfDisabledItem("archive", React.createElement(Icon, { icon: "archive" },
                    React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.archive" })), "ui-czna-users.users.action.archive.selfDisabled")) : (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                        onToggle();
                        handleArchive();
                    } },
                    React.createElement(Icon, { icon: "archive" },
                        React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.archive" }))))),
            canDeleteUser && !isActive && (React.createElement(Button, { buttonStyle: "dropdownItem", onClick: function () {
                    onToggle();
                    handleRestore();
                } },
                React.createElement(Icon, { icon: "arrow-up" },
                    React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.restore" })))),
            canEditUser &&
                isActive &&
                passwordAction("reset-password", "ui-czna-users.users.resetPassword", function () {
                    onToggle();
                    handleResetPassword();
                }),
            canEditUser &&
                isActive &&
                passwordAction("set-password", "ui-czna-users.users.action.setPassword", function () {
                    onToggle();
                    setShowSetPasswordModal(true);
                })));
    };
    var paneContent = function () {
        var _a, _b, _c, _d;
        if (isLoading) {
            return React.createElement(Loading, null);
        }
        if (error || !user) {
            return (React.createElement(FormattedMessage, { id: getLoadFailureMessageId(error, "ui-czna-users.error.userNotFound") }));
        }
        return (React.createElement(AccordionSet, null,
            React.createElement(Accordion, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.section.general" }) },
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 6 },
                        React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.firstName" }), value: user.firstName })),
                    React.createElement(Col, { xs: 6 },
                        React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.lastName" }), value: user.lastName }))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 6 },
                        React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.email" }), value: user.email })),
                    React.createElement(Col, { xs: 6 },
                        React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.phone" }), value: user.phone || "-" }))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 6 },
                        React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.acronym" }), value: user.acronym || "-" }))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 6 },
                        React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.status" }), value: React.createElement(StatusBadge, { status: user.status }) }))),
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 6 },
                        React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.organization" }), value: (_a = organization === null || organization === void 0 ? void 0 : organization.name) !== null && _a !== void 0 ? _a : "—" })))),
            React.createElement(Accordion, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.section.roles" }), displayWhenClosed: React.createElement(Badge, null, ((_b = user.roles) === null || _b === void 0 ? void 0 : _b.length) || 0) }, user.roles && user.roles.length > 0 ? (user.roles.map(function (role) { return (React.createElement(Row, { key: role.id },
                React.createElement(Col, { xs: 6 },
                    React.createElement(FormattedMessage, { id: "ui-czna-users.role.".concat(role.role) })),
                React.createElement(Col, { xs: 6 },
                    React.createElement(RoleValidity, { validFrom: role.validFrom, validTo: role.validTo })))); })) : (React.createElement(FormattedMessage, { id: "ui-czna-users.users.noRoles" }))),
            React.createElement(Accordion, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.section.authorityTypeRoles" }), displayWhenClosed: React.createElement(Badge, null, (_d = (_c = user.authorityTypeRoles) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0) },
                authorityTypes === undefined && authorityTypesFailed && (React.createElement("p", { role: "alert" },
                    React.createElement(FormattedMessage, { id: "ui-czna-users.users.roles.authorityType.detailLoadError" }))),
                authorityTypesLoading ? (React.createElement(Loading, null)) : (React.createElement(MultiColumnList, { columnWidths: {
                        authorityType: "40%",
                        role: "60%",
                    }, columnMapping: {
                        authorityType: (React.createElement(FormattedMessage, { id: "ui-czna-users.users.column.authorityType" })),
                        role: (React.createElement(FormattedMessage, { id: "ui-czna-users.users.roles.section.title" })),
                    }, contentData: authorityTypeRows.map(function (type) {
                        var _a;
                        var assignment = classifyAssignment(rowsOfType(user.authorityTypeRoles, type.code));
                        return {
                            authorityType: authorityTypeLabel(intl, type),
                            // CZNA-1598: the NK role, or every stored row of a non-standard
                            // assignment. An organization we cannot resolve prints as its raw id —
                            // never as "celostátně", which would understate the row's reach.
                            role: (_a = describeAssignment(intl, assignment, function (orgId) {
                                var _a, _b;
                                return (_b = (_a = scopeOrganizations === null || scopeOrganizations === void 0 ? void 0 : scopeOrganizations.items) === null || _a === void 0 ? void 0 : _a.find(function (org) { return org.id === orgId; })) === null || _b === void 0 ? void 0 : _b.name;
                            })) !== null && _a !== void 0 ? _a : "—",
                        };
                    }), visibleColumns: ["authorityType", "role"] }))),
            React.createElement(Accordion, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.users.section.metadata" }) },
                React.createElement(Row, null,
                    React.createElement(Col, { xs: 6 },
                        React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.createdAt" }), value: user.createdAt ? (React.createElement(FormattedDate, { value: user.createdAt })) : ("-") })),
                    React.createElement(Col, { xs: 6 },
                        React.createElement(KeyValue, { label: React.createElement(FormattedMessage, { id: "ui-czna-users.updatedAt" }), value: user.updatedAt ? (React.createElement(FormattedDate, { value: user.updatedAt })) : ("-") })))),
            React.createElement(AuditLogAccordion, { entityId: id })));
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Pane, { defaultWidth: "44%", paneTitle: user ? ("".concat(user.firstName, " ").concat(user.lastName)) : (React.createElement(FormattedMessage, { id: "ui-czna-users.users.detail" })), onClose: onClose, dismissible: true, actionMenu: !isLoading && user ? actionMenu : undefined }, paneContent()),
        user && (React.createElement(React.Fragment, null,
            React.createElement(ConfirmationModal, { open: showArchiveModal, heading: React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.archive.confirm.heading" }), message: React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.archive.confirm.message", values: { name: "".concat(user.firstName, " ").concat(user.lastName) } }), onConfirm: handleConfirmArchive, onCancel: function () { return setShowArchiveModal(false); }, confirmLabel: React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.archive" }), isConfirmButtonDisabled: archiveMutation.isLoading }),
            React.createElement(ConfirmationModal, { open: showRestoreModal, heading: React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.restore.confirm.heading" }), message: React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.restore.confirm.message", values: { name: "".concat(user.firstName, " ").concat(user.lastName) } }), onConfirm: handleConfirmRestore, onCancel: function () { return setShowRestoreModal(false); }, confirmLabel: React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.restore" }), isConfirmButtonDisabled: restoreMutation.isLoading }),
            React.createElement(ConfirmationModal, { open: showResetPasswordModal, heading: React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.resetPassword.confirm.heading" }), message: React.createElement(FormattedMessage, { id: "ui-czna-users.users.action.resetPassword.confirm.message", values: {
                        name: "".concat(user.firstName, " ").concat(user.lastName),
                        email: user.email,
                    } }), onConfirm: handleConfirmResetPassword, onCancel: function () { return setShowResetPasswordModal(false); }, confirmLabel: React.createElement(FormattedMessage, { id: "ui-czna-users.users.resetPassword" }), isConfirmButtonDisabled: resetPasswordMutation.isLoading }),
            React.createElement(ActionErrorModal, { open: isErrorOpen, message: errorMessage, onClose: closeErrorModal }))),
        React.createElement(SetPasswordModal, { open: showSetPasswordModal, onClose: function () { return setShowSetPasswordModal(false); }, onSubmit: function (password, temporary) {
                return setPasswordMutation.mutate({ password: password, temporary: temporary });
            }, isSubmitting: setPasswordMutation.isLoading }),
        React.createElement(Callout, { ref: calloutRef })));
};
export default UserDetailPane;
